/**
 * VoiceButton - React component for voice interaction
 */
import React, { useState, useEffect, useRef } from 'react';
import VoiceSDK_v2 from '../v2/VoiceSDK.js';

const VoiceButton = ({ 
  signedUrl,
  websocketUrl, // Deprecated: use signedUrl instead
  agentId, // Optional - for direct agent access (unsecured method)
  appId, // Required - User's app ID for authentication
  voice = 'default',
  language = 'en',
  autoReconnect = true,
  
  // NEW: Agent settings override (only available with signed link)
  agentSettingsOverride = null,
  
  // Visual Assistant Configuration
  visualAssistant = null,
  
  onConnected,
  onDisconnected,
  onRecordingStarted,
  onRecordingStopped,
  onPlaybackStarted,
  onPlaybackStopped,
  onError,
  onMessage,
  onBargeIn,
  onStopPlaying,
  onStartConversation,
  onEndConversation,
  className = '',
  style = {},
  children
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  
  const voiceSDKRef = useRef(null);
  
  // Initialize VoiceSDK
  useEffect(() => {
    console.log(`🎙️ VoiceButton: Creating VoiceSDK_v2 instance for ${websocketUrl || signedUrl}`);
    
    // Clean up existing instance if any
    if (voiceSDKRef.current) {
      console.log(`🎙️ VoiceButton: Destroying existing VoiceSDK_v2 instance`);
      voiceSDKRef.current.destroy();
      voiceSDKRef.current = null;
    }
    
    const voiceSDK = new VoiceSDK_v2({
      signedUrl: websocketUrl || signedUrl, // Support both for backward compatibility
      websocketUrl: websocketUrl || signedUrl, // Keep for internal use
      agentId, // Pass through agentId if provided
      appId, // Required for authentication
      voice,
      language,
      autoReconnect,
      agentSettingsOverride, // Pass through agent settings override
      visualAssistant, // Pass through visual assistant config
      onStartConversation, // Pass through hooks
      onEndConversation
    });
    
    // Setup event listeners
    voiceSDK.on('connected', () => {
      setIsConnected(true);
      setConnectionStatus('Connected');
      onConnected?.();
    });
    
    voiceSDK.on('disconnected', () => {
      setIsConnected(false);
      setConnectionStatus('Disconnected');
      onDisconnected?.();
    });
    
    voiceSDK.on('recordingStarted', () => {
      setIsRecording(true);
      onRecordingStarted?.();
    });
    
    voiceSDK.on('recordingStopped', () => {
      setIsRecording(false);
      onRecordingStopped?.();
    });
    
    voiceSDK.on('playbackStarted', () => {
      setIsPlaying(true);
      onPlaybackStarted?.();
    });
    
    voiceSDK.on('playbackStopped', () => {
      setIsPlaying(false);
      onPlaybackStopped?.();
    });
    
    voiceSDK.on('error', (error) => {
      onError?.(error);
    });
    
    voiceSDK.on('message', (message) => {
      onMessage?.(message);
    });
    
    voiceSDK.on('bargeIn', (message) => {
      onBargeIn?.(message);
    });
    
    voiceSDK.on('stopPlaying', (message) => {
      onStopPlaying?.(message);
    });
    
    voiceSDKRef.current = voiceSDK;
    
    // Auto-connect
    voiceSDK.connect();
    
    // Cleanup on unmount
    return () => {
      const url = signedUrl || websocketUrl;
      console.log(`🎙️ VoiceButton: Cleaning up VoiceSDK instance for ${url}`);
      if (voiceSDKRef.current) {
        voiceSDKRef.current.destroy();
        voiceSDKRef.current = null;
      }
    };
  }, [signedUrl, websocketUrl, agentId, appId, voice, language, agentSettingsOverride, visualAssistant]);
  
  // Handle button click
  const handleClick = async () => {
    if (!voiceSDKRef.current) return;
    
    try {
      await voiceSDKRef.current.toggleRecording();
    } catch (error) {
      console.error('Error toggling recording:', error);
    }
  };
  
  // Default button content
  const defaultContent = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div style={{ fontSize: '20px' }}>
        {isRecording ? '🔴' : '🎤'}
      </div>
      <div>
        {isRecording ? 'Stop Listening' : 'Start Listening'}
      </div>
    </div>
  );
  
  return (
    <button
      className={`voice-button ${isRecording ? 'recording' : ''} ${className}`}
      style={{
        padding: '12px 24px',
        border: 'none',
        borderRadius: '8px',
        backgroundColor: isRecording ? '#dc3545' : '#007bff',
        color: 'white',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '500',
        transition: 'all 0.2s ease',
        ...style
      }}
      onClick={handleClick}
      disabled={!isConnected}
    >
      {children || defaultContent}
    </button>
  );
};

export default VoiceButton;
